import { Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/hooks/useTranslation";
import { toast } from "sonner";
import { withTrace } from "@/hooks/useTraceSection";

interface QrCodeDownloadSectionProps {
  qrCodeDataUrl: string | null;
  loading?: boolean;
  eventUuid?: string;
  eventName?: string;
}

export function QrCodeDownloadSection({
  qrCodeDataUrl,
  loading = false,
  eventUuid,
}: QrCodeDownloadSectionProps) {
  const { t } = useTranslation();

  const handleDownloadQrCode = () => {
    if (!qrCodeDataUrl) return;

    void withTrace("shareModal", "downloadQrCode", (span) => {
      span.setAttribute("event.uuid", eventUuid ?? "");
      try {
        const link = document.createElement("a");
        link.href = qrCodeDataUrl;
        link.download = `jubiel-event-qr-code-${eventUuid || "event"}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success(t("events.share.qrDownloaded"));
      } catch (error) {
        const err = error as Error;
        span.recordException(err);
        console.error("Failed to download QR code:", err);
        toast.error(t("events.share.qrDownloadError"));
      }
    }, {
      eventUuid: eventUuid ?? "",
    });
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-xs font-medium">
        <Download className="h-4 w-4" />
        <span>{t("events.share.printed")}</span>
      </div>
      
      <p className="text-xs text-muted-foreground">
        {t("events.share.printedDescription")}
      </p>
      {qrCodeDataUrl ? (
        <div className="flex flex-col items-center gap-3">
          <div className="p-4 bg-white rounded-lg">
            <img
              src={qrCodeDataUrl}
              alt={t("events.share.qrCodeLabel")}
              className="w-[150px] h-[150px]"
            />
          </div>
          <Button
            variant="outline"
            onClick={handleDownloadQrCode}
            disabled={!qrCodeDataUrl || loading}
            className="w-full text-sm"
          >
            <Download className="mr-2 h-4 w-4" />
            <span className="text-xs">{t("events.share.downloadQrCode")}</span>
          </Button>
        </div>
      ) : (
        <div className="text-center py-8 text-xs text-muted-foreground">
          {loading ? t("common.loading") : t("events.share.qrCodeNotAvailable")}
        </div>
      )}
    </div>
  );
}

